import {Component, EventEmitter, Input, Output, ElementRef, ViewChild} from '@angular/core';
import {S3ImageBlob, S3UploaderRatioEnum, S3UploaderRatios, S3UploaderTypes, S3UploaderTypesEnum} from './s3-uploader.types';
import {MatDialog} from '@angular/material/dialog';
import {S3ImageEditorComponent} from '../image-editor/s3-image-editor.component';
import {ImageService} from '../../../services/image/image.service';
import {SnackService} from '../../../services/snack/snack.service';
import {UtilService} from '../../../services/util/util.service';
import {TranslateService} from '@ngx-translate/core';
import {EventImageType, ImageIdResponse, LegalImageType, VenueImageType} from '../../../generated/sales-terminal-client';
import {finalize} from 'rxjs';
import {DirtyStateTrackerService} from '../../../services/dirty-state-tracker/dirty-state-tracker.service';
import heic2any from "heic2any";
import {ViewService} from '../../../services/view/view.service';

@Component({
  selector: 'app-s3-uploader',
  templateUrl: './s3-uploader.component.html',
  styleUrls: ['./s3-uploader.component.scss']
})
export class S3UploaderComponent {
  public static FILE_SIZE_LIMIT: number = 2097152; // 2MB
  @ViewChild('uploader') uploader: ElementRef;

  @Input() public title: string = 'general.api_uploader_title';
  @Input() public titleHint: string;
  @Input() public isSubTitle: boolean;
  @Input() public required: boolean;
  @Input() public button: string = 'general.api_uploader_select_file';
  @Input() public hint: string = 'general.api_uploader_hint';
  @Input() public model: any;
  @Input() public id: number;
  @Input() public ownerCentreId: number;
  @Input() public type: S3UploaderTypesEnum = S3UploaderTypes.Venue;
  @Input() public ratio: S3UploaderRatioEnum = S3UploaderRatios.Modern;
  @Input() public canDelete: boolean = true;
  @Input() public disabled: boolean;
  @Input() public multipleRatios: boolean;
  @Output() public modelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public modelSaved: EventEmitter<any> = new EventEmitter<any>();

  public loadedImage: S3ImageBlob = {};
  public S3UploaderTypes = S3UploaderTypes;
  public S3UploaderRatios = S3UploaderRatios;
  public isLoading: boolean;

  private _isSaving: boolean;
  public set isSaving(value: boolean) {
    this._isSaving = value;
    this.viewService.triggerCdr();
  }
  public get isSaving(): boolean { return this._isSaving; }

  public isDirty: boolean;
  @Input() public dirtyParent: string;
  @Input() public dirtyService: DirtyStateTrackerService;
  @Input() public set dirtyTracker(value: any) {
    if (this.dirtyService) {
      this.isDirty = this.dirtyService.isParentDirty(this.isDirty, this.dirtyParent);
    }
  }

  get acceptedMimeTypes(): string {
    return 'image/png,image/jpeg,image/heic,image/webp';
  }

  constructor(private dialog: MatDialog,
              private imageService: ImageService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private viewService: ViewService,
              public utilService: UtilService) {}

  public openUploader(): void {
    this.uploader.nativeElement.click();
  }

  public resizeImage(): void {
    this.openImageEditor(this.loadedImage, true);
  }

  public delayedLoaderUpdate(value: boolean): void {
    setTimeout(() => {
      this.isLoading = value;
    }, 0);
  }

  public get anyImageLoaded(): boolean {
    return !!(this.loadedImage && Object.values(this.loadedImage).filter(blob => !!blob).length);
  }

  public get allImagesLoaded(): boolean {
    return Object.values(this.loadedImage).filter(blob => !!blob).length === (this.multipleRatios ? 3 : 1);
  }

  public removeImage(): void {
    this.isSaving = true;
    if (this.type === S3UploaderTypes.Venue) {
      this.deleteVenueImage();
    } else if (this.type === S3UploaderTypes.Event) {
      this.deleteEventImages();
    }
  }

  public processFile($event: Event): void {
    this.isSaving = true;
    const target = $event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files.length) {
      let file: File = files[0];
      let converted: Promise<void>;
      if (file.size > S3UploaderComponent.FILE_SIZE_LIMIT) {
        this.snackService.showError(this.translateService.instant('general.api_uploader_file_size_too_large'));
        this.isSaving = false;
        return;
      }
      if (UtilService.isHEICImage(file)) {
        // .heic images have zero browser support, have to transform it into some supported format first
        converted = heic2any({blob: files[0], toType:"image/jpeg"}).then((jpgBlob: Blob | Blob[]) => {
          file = this.convertBlobToFile(jpgBlob, this.getFileNameAsJpg(file));
        }).catch(() => {
          this.snackService.showError(this.translateService.instant('general.api_uploader_error'));
          this.isSaving = false;
        });
      } else {
        converted = Promise.resolve();
      }

      converted.then(() => {
        const images: S3ImageBlob = {};
        images[this.ratio] = file;
        this.openImageEditor(images);
      })
    }
  }

  private getFileNameAsJpg(file: File) {
    return file.name.replace(/\.[^/.]+$/, ".jpg");
  }

  private convertBlobToFile(blob: Blob | Blob[], fileName: string) : File {
    return this.blobToFile(blob instanceof Blob ? blob : blob[0], fileName)
  }

  private blobToFile(blob: Blob, fileName: string): File {
    const b: any = blob;
    b.lastModified = new Date();
    b.name = fileName;
    return <File>blob;
  }

  private openImageEditor(imageBlob: S3ImageBlob, resize?: boolean): void {
    const dialogRef = this.dialog.open(S3ImageEditorComponent, {
      data: { imageBlob, ratio: this.ratio, type: this.type, resize, multipleRatios: this.multipleRatios }
    });

    dialogRef.afterClosed().subscribe((imagesOutput => {
      if (imagesOutput && Object.keys(imagesOutput)?.length) {
        this.isSaving = true;
        if (this.type === S3UploaderTypes.Venue && imagesOutput[this.ratio]) {
          this.selfServiceUploadVenueImage(imagesOutput);
        } else if (this.type === S3UploaderTypes.Event) {
          this.selfServiceUploadEventImages(imagesOutput);
        } else if (this.type === S3UploaderTypes.Legal) {
          this.selfServiceUploadLegalImages(imagesOutput);
        }
      } else {
        this.isSaving = false;
      }
      this.uploader.nativeElement.value = '';
    }));
  }

  private selfServiceUploadVenueImage(imagesOutput: S3ImageBlob): void {
    const imageType: VenueImageType = this.ratio === S3UploaderRatios.Logo ? VenueImageType.Logo : VenueImageType.Location;
    this.imageService.getVenueImageId(this.id, imageType).subscribe((res: ImageIdResponse) => {
      const ratios: string[] = Object.keys(imagesOutput);
      let succeed: number = 0, finished: number = 0;
      ratios.forEach(ratio => {
        this.imageService.selfServiceUploadVenueImage(this.id, imageType, res.id, ratio, imagesOutput[ratio]).pipe(
          finalize(() => {
            finished++;
            if (finished === ratios.length) {
              this.isSaving = false;
            }
          })
        ).subscribe(() => {
          succeed++;
          if (succeed === ratios.length) {
            this.snackService.showSuccess(this.translateService.instant('general.api_uploader_success'));
            this.emitModelChange(res.id);
          }
        });
      });
    });
  }

  private deleteVenueImage(): void {
    this.imageService.deleteVenueImage(this.id, this.ratio === S3UploaderRatios.Logo ? VenueImageType.Logo : VenueImageType.Location).pipe(
      finalize(() => {
        this.isSaving = false;
      })
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_uploader_delete_success'));
      this.emitModelChange(null);
      this.loadedImage = {};
    });
  }

  private selfServiceUploadEventImages(imagesOutput: S3ImageBlob): void {
    const imageType: EventImageType = this.ratio === S3UploaderRatios.Email ? EventImageType.PurchaseEmail : EventImageType.Cover;
    this.imageService.getEventImageId(this.id, imageType).subscribe((res: ImageIdResponse) => {
      const ratios: string[] = Object.keys(imagesOutput);
      let succeed: number = 0, finished: number = 0;
      ratios.forEach(ratio => {
        this.imageService.selfServiceUploadEventImage(this.id, res.id, imageType === EventImageType.PurchaseEmail ? null : ratio, imageType, imagesOutput[ratio]).pipe(
          finalize(() => {
            finished++;
            if (finished === ratios.length) {
              this.isSaving = false;
            }
          })
        ).subscribe((uploadResponse) => {
          succeed++;
          if (succeed === ratios.length) {
            this.snackService.showSuccess(this.translateService.instant('general.api_uploader_success'));
            this.emitModelChange(uploadResponse.id);
            this.modelSaved.emit();
          }
        });
      });
    });
  }

  private deleteEventImages(): void {
    this.imageService.deleteEventImage(this.id).pipe(
      finalize(() => {
        this.isSaving = false;
      })
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_uploader_delete_success'));
      this.emitModelChange(null);
      this.loadedImage = {};
    });
  }

  private selfServiceUploadLegalImages(imagesOutput: S3ImageBlob): void {
    this.imageService.getLegalPersonImageId(this.id).subscribe((res: ImageIdResponse) => {
      const ratios: string[] = Object.keys(imagesOutput);
      let succeed: number = 0, finished: number = 0;
      ratios.forEach(ratio => {
        this.imageService.uploadLegalPersonImage(this.id, res.id, null, LegalImageType.PurchaseEmail, imagesOutput[ratio]).pipe(
          finalize(() => {
            finished++;
            if (finished === ratios.length) {
              this.isSaving = false;
            }
          })
        ).subscribe((uploadResponse) => {
          succeed++;
          if (succeed === ratios.length) {
            this.snackService.showSuccess(this.translateService.instant('general.api_uploader_success'));
            this.emitModelChange(uploadResponse.id);
          }
        });
      });
    });
  }

  private emitModelChange(imageId: string): void {
    this.model = imageId;
    this.modelChange.emit(this.model);
    this.isDirty = true;
  }

  public get requiredButEmpty(): boolean {
    return this.required && this.isDirty && !this.anyImageLoaded;
  }
}
